import { IonInput } from "@ionic/react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { getErrorText } from "@/components/atoms/form-ion/utils";
import { useFieldContext } from "@/form/common/hook";
import type { AppFormFieldLabelProps } from "@/form/components/field-label";
import type { AppFormFieldProps } from "@/form/components/types";
import { cn } from "@/lib/style/cva.config";

export type InputFieldProps = AppFormFieldLabelProps & {
  withAsterisk?: boolean;
  withPlaceholder?: boolean;
  withHelperText?: boolean;
};

export const InputField = forwardRef<
  HTMLIonInputElement,
  React.ComponentProps<typeof IonInput> & AppFormFieldProps & InputFieldProps
>(function InputField(
  { withAsterisk = true, withPlaceholder = false, withHelperText = false, ...props },
  ref,
) {
  const { t, i18n } = useTranslation();
  const field = useFieldContext<string>();

  return (
    <IonInput
      ref={ref}
      labelPlacement={props.labelPlacement ?? "stacked"}
      label={t(`fields:${props.name ?? field.name}.label`)}
      placeholder={
        withPlaceholder ? t(`fields:${props.name ?? field.name}.placeholder`) : undefined
      }
      helperText={withHelperText ? t(`fields:${props.name ?? field.name}.helperText`) : undefined}
      name={field.name}
      value={field.state.value}
      onIonChange={(event) => field.handleChange(String(event.detail.value))}
      onIonBlur={field.handleBlur}
      readonly={field.state.meta.isValidating || field.form.state.isSubmitting}
      errorText={getErrorText({ language: i18n.language, errors: field.state.meta.errors })}
      {...props}
      className={cn(
        withAsterisk && "ion-required",
        field.state.meta.isTouched && "ion-touched",
        !!field.state.meta.errors && field.state.meta.errors.length > 0 && "ion-invalid",
        props.className,
      )}
    />
  );
});
